const SEARCH_HIGHLIGHT_SELECTOR = 'mark[data-md-search-highlight="true"]'

function setSearchHighlightStyles(highlight: HTMLElement) {
  highlight.style.backgroundColor = 'rgba(249, 115, 22, 0.35)'
  highlight.style.borderRadius = '0.125rem'
  highlight.style.paddingInline = '0.125rem'
  highlight.style.color = 'inherit'
}

export function clearActiveSearchHighlights(container: HTMLElement) {
  container.querySelectorAll<HTMLElement>(SEARCH_HIGHLIGHT_SELECTOR).forEach((highlight) => {
    const parent = highlight.parentNode
    if (!parent) {
      return
    }
    while (highlight.firstChild) {
      parent.insertBefore(highlight.firstChild, highlight)
    }
    parent.removeChild(highlight)
    parent.normalize()
  })
}

function getSearchableTextNodes(container: HTMLElement): Text[] {
  const treeWalker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parentElement = node.parentElement
      if (!parentElement) {
        return NodeFilter.FILTER_REJECT
      }
      if (parentElement.closest('code, pre, svg, script, style, textarea, [data-md-search-highlight="true"]')) {
        return NodeFilter.FILTER_REJECT
      }
      return node.textContent ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT
    },
  })

  const textNodes: Text[] = []
  let currentNode = treeWalker.nextNode()

  while (currentNode) {
    textNodes.push(currentNode as Text)
    currentNode = treeWalker.nextNode()
  }

  return textNodes
}

function findCaseInsensitiveOccurrence(text: string, query: string, occurrence: number): number {
  if (!query || occurrence < 0) {
    return -1
  }

  const normalizedText = text.toLocaleLowerCase()
  const normalizedQuery = query.toLocaleLowerCase()
  let searchFrom = 0

  for (let currentOccurrence = 0; currentOccurrence <= occurrence; currentOccurrence += 1) {
    const matchIndex = normalizedText.indexOf(normalizedQuery, searchFrom)
    if (matchIndex === -1) {
      return -1
    }
    if (currentOccurrence === occurrence) {
      return matchIndex
    }
    searchFrom = matchIndex + 1
  }

  return -1
}

type SourceRange = {
  start: number
  end: number
}

function getHiddenMarkdownRanges(source: string): SourceRange[] {
  const ranges: SourceRange[] = []
  const lines = source.split('\n')
  let fenceStart: number | undefined
  let offset = 0

  lines.forEach((line, lineIndex) => {
    const lineEnd = offset + line.length + (lineIndex < lines.length - 1 ? 1 : 0)
    if (/^```/.test(line)) {
      if (fenceStart === undefined) {
        fenceStart = offset
      } else {
        ranges.push({ start: fenceStart, end: lineEnd })
        fenceStart = undefined
      }
    }
    offset = lineEnd
  })

  if (fenceStart !== undefined) {
    ranges.push({ start: fenceStart, end: source.length })
  }

  const inlineCodePattern = /(`+)([\s\S]*?)\1/g
  let inlineCodeMatch: RegExpExecArray | null
  while ((inlineCodeMatch = inlineCodePattern.exec(source)) !== null) {
    ranges.push({
      start: inlineCodeMatch.index,
      end: inlineCodeMatch.index + inlineCodeMatch[0].length,
    })
  }

  const linkDestinationPattern = /\]\(\s*(?:<([^>\n]*)>|([^\s)\n]+))/g
  let linkDestinationMatch: RegExpExecArray | null
  while ((linkDestinationMatch = linkDestinationPattern.exec(source)) !== null) {
    const destination = linkDestinationMatch[1] ?? linkDestinationMatch[2]
    if (!destination) {
      continue
    }
    const destinationStart = linkDestinationMatch.index + linkDestinationMatch[0].indexOf(destination)
    ranges.push({ start: destinationStart, end: destinationStart + destination.length })
  }

  return ranges
}

function overlapsSourceRange(ranges: SourceRange[], start: number, end: number): boolean {
  return ranges.some((range) => range.start < end && start < range.end)
}

function countVisibleCaseInsensitiveOccurrences(
  text: string,
  query: string,
  beforeOffset: number,
  hiddenRanges: SourceRange[]
): number {
  const normalizedText = text.toLocaleLowerCase()
  const normalizedQuery = query.toLocaleLowerCase()
  let count = 0
  let searchFrom = 0

  while (searchFrom <= normalizedText.length) {
    const matchIndex = normalizedText.indexOf(normalizedQuery, searchFrom)
    if (matchIndex === -1 || matchIndex >= beforeOffset) {
      return count
    }
    if (!overlapsSourceRange(hiddenRanges, matchIndex, matchIndex + query.length)) {
      count += 1
    }
    searchFrom = matchIndex + 1
  }

  return count
}

function getTextPointAtOffset(textNodes: Text[], offset: number): { node: Text; offset: number } | undefined {
  let remaining = offset

  for (const node of textNodes) {
    const nodeLength = node.textContent?.length ?? 0
    if (remaining <= nodeLength) {
      return { node, offset: remaining }
    }
    remaining -= nodeLength
  }

  const lastNode = textNodes[textNodes.length - 1]
  return lastNode ? { node: lastNode, offset: lastNode.textContent?.length ?? 0 } : undefined
}

export function highlightActiveSearchMatch(
  container: HTMLElement,
  sectionText: string,
  activeRange: { start: number; end: number } | undefined
) {
  clearActiveSearchHighlights(container)

  if (!activeRange || activeRange.start >= activeRange.end) {
    return
  }

  const matchText = sectionText.slice(activeRange.start, activeRange.end)
  if (!matchText) {
    return
  }

  const hiddenRanges = getHiddenMarkdownRanges(sectionText)
  if (overlapsSourceRange(hiddenRanges, activeRange.start, activeRange.end)) {
    return
  }

  const textNodes = getSearchableTextNodes(container)
  const visibleText = textNodes.map((node) => node.textContent ?? '').join('')
  const targetOccurrence = countVisibleCaseInsensitiveOccurrences(
    sectionText,
    matchText,
    activeRange.start,
    hiddenRanges
  )
  const visibleMatchStart = findCaseInsensitiveOccurrence(visibleText, matchText, targetOccurrence)

  if (visibleMatchStart < 0) {
    return
  }

  const startPoint = getTextPointAtOffset(textNodes, visibleMatchStart)
  const endPoint = getTextPointAtOffset(textNodes, visibleMatchStart + matchText.length)
  if (!startPoint || !endPoint) {
    return
  }

  const range = document.createRange()
  range.setStart(startPoint.node, startPoint.offset)
  range.setEnd(endPoint.node, endPoint.offset)

  const mark = document.createElement('mark')
  mark.dataset.mdSearchHighlight = 'true'
  setSearchHighlightStyles(mark)
  mark.appendChild(range.extractContents())
  range.insertNode(mark)
  mark.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
}
